import Select from '../ui/Select';
import { DID_NOT_TRIP_DEFECT } from '../../lib/testValidators';

/**
 * RCD evidence grid for RecordTestModal: push button, trip time, and the
 * failure-reason dropdown — extracted from RecordTestModal.jsx (Brief 44).
 *
 * Brief 37 dropped the visual pass/fail gate. What used to be a separate
 * Visual Inspection radio group and a "did not trip" checkbox are now one
 * failure-reason dropdown — the same single-select pattern every other
 * asset type uses (`Did Not Trip`, `Poor Condition`, `Poor Fitment`, `Other`).
 *
 * Props:
 *   register — react-hook-form register fn.
 *   errors — react-hook-form formState.errors.
 *   rcdPushAnswered — whether the push button has a Pass/Fail answer yet
 *     (drives the "Not yet tested" hint).
 *   rcdPushButton — current watched push-button value, used to disable/
 *     placeholder the trip time input once it fails.
 *   rcdFailureReason — current watched failure-reason value; selecting
 *     `Did Not Trip` also disables/clears the trip time input.
 *   failureOptions — RCD failure reason options (`FAILURE_OPTIONS.rcd`).
 *   rcdOriginalText — set when an existing record's stored text couldn't be
 *     matched to exactly one option after stripping the derived reasons;
 *     shown read-only so an edit is never made blind. See Brief 37 Part 4.
 */
export default function RcdEvidenceFields({
  register,
  errors,
  rcdPushAnswered,
  rcdPushButton,
  rcdFailureReason,
  failureOptions,
  rcdOriginalText,
}) {
  const didNotTrip = rcdFailureReason === DID_NOT_TRIP_DEFECT;
  const tripTimeDisabled = rcdPushButton === 'fail' || didNotTrip;

  return (
    <div className="form-group">
      <span className="form-label-block">Evidence</span>
      <div className="rcd-evidence-grid">
        {/* Push Button — required: a technician must actually perform it */}
        <div className="rcd-evidence-row">
          <span className="rcd-evidence-label">Push Button *</span>
          <div className="rcd-evidence-input-wrap">
            <div
              className={`segmented-control segmented-control-modal ${errors.rcdPushButton ? 'form-group-required-highlight' : ''}`.trim()}
            >
              <label className="segmented-option-label">
                <input
                  type="radio"
                  value="pass"
                  {...register('rcdPushButton', { required: 'Perform the push button test and record Pass or Fail' })}
                  className="segmented-radio"
                />
                <span>Pass</span>
              </label>
              <label className="segmented-option-label">
                <input
                  type="radio"
                  value="fail"
                  {...register('rcdPushButton', { required: 'Perform the push button test and record Pass or Fail' })}
                  className="segmented-radio"
                />
                <span>Fail</span>
              </label>
            </div>
            {errors.rcdPushButton ? (
              <span className="form-error">{errors.rcdPushButton.message}</span>
            ) : !rcdPushAnswered ? (
              <span className="text-muted text-xs">Not yet tested</span>
            ) : null}
          </div>
        </div>

        {/* Trip Time */}
        <div className="rcd-evidence-row">
          <label className="rcd-evidence-label" htmlFor="rcd-trip-time-ms">Trip Time (ms)</label>
          <div className="rcd-evidence-input-wrap">
            <input
              id="rcd-trip-time-ms"
              type="number"
              min={0}
              step={1}
              placeholder={tripTimeDisabled ? 'N/A – did not trip' : 'ms'}
              className="rcd-trip-time-input"
              disabled={tripTimeDisabled}
              {...register('rcdTripTimeMs')}
            />
            {errors.rcdTripTimeMs && (
              <span className="form-error">{errors.rcdTripTimeMs.message}</span>
            )}
          </div>
        </div>
      </div>

      <Select
        id="record-test-rcd-failure-reason"
        label="Failure Reason"
        {...register('rcdFailureReason')}
      >
        <option value="">No additional reason</option>
        {failureOptions.map((reason) => (
          <option key={reason} value={reason}>
            {reason}
          </option>
        ))}
      </Select>
      {rcdOriginalText && (
        <p className="text-muted" style={{ marginTop: 4 }}>
          Originally recorded: &quot;{rcdOriginalText}&quot;. Select a reason above to update it,
          or leave as is to keep the original record unchanged.
        </p>
      )}
    </div>
  );
}
